import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore, createKeyedCache } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { bankAccountService } from '../services/api';
import { storeMessages } from './storeMessages';

export const BANK_ACCOUNT_TYPES = ['CHECKING', 'SAVINGS', 'FIXED_DEPOSIT', 'LOAN', 'CREDIT_LINE'] as const;
export const TRANSACTION_TYPES = ['DEPOSIT', 'WITHDRAWAL', 'TRANSFER_IN', 'TRANSFER_OUT', 'FEE', 'INTEREST'] as const;
export interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currency: string;
  openingBalance: number | string;
  currentBalance: number | string;
  isActive: boolean;
  description?: string | null;
  unreconciledCount?: number;
  _count?: { transactions: number };
}

export interface BankTransaction {
  id: string;
  bankAccountId: string;
  transactionDate: string;
  description: string;
  amount: number | string;
  balanceAfter: number | string;
  reference: string;
  transactionType: string;
  isReconciled: boolean;
  /** 發票收款產生的交易：要從發票刪除收款，不能在這裡改刪 */
  invoicePayment?: { invoice: { number: string } } | null;
  /** 折讓退款產生的交易（同上，要從發票刪除折讓） */
  creditNoteRefund?: { number: string } | null;
}

export const useBankAccountStoreBase = createEntityStore<BankAccount>({
  entityName: 'bankAccount',
  fetchFn: (page: number) => bankAccountService.getBankAccounts(page),
  deleteFn: bankAccountService.deleteBankAccount,
  toast,
  messages: storeMessages,
});

/** 銀行帳戶合計（列表上方一行摘要） */
export const useBankSummary = createKeyedCache('accounting-bank-summary', async (_q: { all: true }) =>
  (await bankAccountService.getBankAccountsSummary()).data as { totalBalance: number; activeAccounts: number });

/** 單一帳戶（交易明細頁頁首）；先顯示快取再背景更新 */
export const useBankAccountDetail = createKeyedCache('accounting-bank-account', async (q: { id: string }) =>
  (await bankAccountService.getBankAccountById(q.id)).data as BankAccount);

/** 每個帳戶一個交易列表 store（同一個帳戶共用，切換帳戶不會混到別的帳戶的快取） */
const transactionStores: Record<string, ReturnType<typeof createTransactionStore>> = {};
const createTransactionStore = (accountId: string) => createEntityStore<BankTransaction>({
  entityName: `bankTransactions-${accountId}`,
  fetchFn: (page: number) => bankAccountService.getBankTransactions(accountId, page),
  deleteFn: (id: string) => bankAccountService.deleteBankTransaction(accountId, id),
  toast,
  messages: storeMessages,
});
export const transactionStoreFor = (accountId: string) => {
  if (!transactionStores[accountId]) transactionStores[accountId] = createTransactionStore(accountId);
  return transactionStores[accountId];
};

/**
 * 銀行帳戶下拉選單（對帳管理用）：stale-while-revalidate，同時只送一個請求（R31）
 */
interface BankAccountOptionsState {
  accounts: BankAccount[];
  loaded: boolean;
  fetchAll: () => Promise<void>;
}
let pendingFetch: Promise<void> | null = null;
export const useBankAccountOptionsStore = create<BankAccountOptionsState>()(
  persist(
    (set) => ({
      accounts: [],
      loaded: false,
      fetchAll: () => {
        if (!pendingFetch) {
          pendingFetch = bankAccountService.getBankAccounts(1, 1000)
            .then(response => set({ accounts: response.data.data ?? [], loaded: true }))
            .catch(error => console.error('Failed to load bank account options:', error))
            .finally(() => { pendingFetch = null; });
        }
        return pendingFetch;
      },
    }),
    { name: 'bank-account-options', version: 1, partialize: state => ({ accounts: state.accounts, loaded: state.loaded }) }
  )
);
